import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "./components/ContactForm";
import { dramas } from "@/mocks/dramas";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const stats = [
  { value: `${dramas.length}+`, label: "收录影视作品" },
  { value: `${newsList.length}+`, label: "影视资讯动态" },
  { value: "16+", label: "细分内容类型" },
  { value: "7", label: "内容地区覆盖" },
];

const values = [
  {
    icon: "ri-time-line",
    title: "及时更新",
    desc: "紧盯最新电视剧与行业动态，第一时间为影视爱好者同步作品资讯。",
  },
  {
    icon: "ri-shield-check-line",
    title: "权威可靠",
    desc: "内容围绕作品本身展开，信息准确、结构清晰，方便快速查阅与了解。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "内容全面",
    desc: "覆盖电视剧、电影与经典影视，提供剧情介绍、演职人员与相关推荐。",
  },
  {
    icon: "ri-smartphone-line",
    title: "多端适配",
    desc: "桌面与移动端均保持一致体验，随时随地方便浏览影视内容。",
  },
];

const notices = [
  "本站内容仅用于影视资讯查询与剧情介绍参考，不提供真实影视播放、下载或在线观看功能。",
  "站内的“收藏”功能为本地浏览器演示，收藏状态保存在你自己的设备中，无需登录且不会上传。",
  "“分享”功能为演示效果，不会真实分享到任何第三方平台，也不会产生任何数据交互。",
  "影视作品信息与资讯内容会持续更新，如发现内容有误，欢迎通过下方反馈表单与我们联系。",
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 赏金女王影视大全",
    description:
      "了解赏金女王影视大全的平台定位、内容范围与使用须知，并获取联系方式与反馈渠道。我们致力于为影视爱好者提供权威及时的影视资讯与剧情介绍。",
    keywords: "关于我们,赏金女王,影视平台介绍,联系方式,使用须知",
  });

  return (
    <div className="w-full">
      <div className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="mb-6">
            <Breadcrumb tone="dark" items={[{ name: "首页", path: "/" }, { name: "关于我们" }]} />
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 max-w-3xl">
            关于赏金女王影视大全
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-primary-100 leading-relaxed">
            赏金女王是一个面向广大影视爱好者与追剧用户的综合影视内容平台。我们围绕最新电视剧、影视资讯与剧情介绍，
            提供权威及时的作品库与动态内容，帮助你高效查找影视作品、了解剧情故事、掌握行业动向。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14 space-y-14 md:space-y-16">
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-lg bg-background-50 border border-background-200 p-5 text-center"
            >
              <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{s.value}</p>
              <p className="mt-2 text-xs md:text-sm text-foreground-600">{s.label}</p>
            </div>
          ))}
        </section>

        <section>
          <SectionHeading title="网站简介" subtitle="我们提供什么，为什么做这件事" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200 p-5 md:p-8">
              <p className="text-sm md:text-base text-foreground-700 leading-7 md:leading-8">
                赏金女王影视大全聚焦于影视作品的信息整理与内容分享。我们的核心用户是关注最新电视剧与影视资讯的年轻及中年群体，
                他们希望通过一个信息密度适中、结构清晰的平台，快速了解某部作品的题材、演职人员、剧情概要以及相关的行业动态。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-7 md:leading-8">
                因此，我们把内容范围集中在三类：一是覆盖电视剧与电影的影视作品库，二是围绕作品的剧情介绍与看点整理，
                三是影视行业的最新资讯与市场观察。我们希望用清晰的信息层级与真实的图文内容，
                帮助用户在做“看什么”的决策时更有参考依据。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg bg-background-50 border border-background-200 p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                      <i className={v.icon} aria-hidden="true"></i>
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-950">{v.title}</h3>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <SectionHeading title="联系方式" subtitle="欢迎通过以下方式与我们取得联系" />
            <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 space-y-4">
              {[
                { icon: "ri-mail-line", label: "反馈邮箱", value: "feedback@shangjinnvwang.example" },
                { icon: "ri-user-voice-line", label: "内容合作", value: "media@shangjinnvwang.example" },
                { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区影视文化产业园 A 座" },
                { icon: "ri-time-line", label: "内容更新", value: "每日 09:00 - 21:00 持续更新" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                    <i className={c.icon} aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading title="意见反馈" subtitle="内容纠错、功能建议与合作咨询" />
            <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <ContactForm />
            </div>
          </div>
        </section>

        <section>
          <SectionHeading title="使用须知" subtitle="使用本站前，请先了解以下说明" />
          <div className="rounded-lg bg-primary-50 border border-primary-100 p-5 md:p-8">
            <ul className="space-y-3">
              {notices.map((n) => (
                <li key={n} className="flex items-start gap-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <i className="ri-information-line text-primary-600 mt-0.5 shrink-0" aria-hidden="true"></i>
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}